import type { IPreferredServiceOrganizationProvider } from 'frontend/components/interfaces/preferredServiceOrganizationProvider';
import type { IServiceConsumer } from 'frontend/components/interfaces/serviceConsumer';
import type { IServiceOrganizationSettings } from 'frontend/components/interfaces/serviceOrganizationSettings';
import type { IServiceProvider } from 'frontend/components/interfaces/serviceProvider';
import { Button, Card, Text } from '@ksyos/design-system';
import { ActionBarShowAndRemoveSelection } from 'frontend/components/userDataManagement/ActionBarShowAndRemoveSelection';
import { orderBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization } from '@common/enums';
import { preferredServiceOrganizationProviderRemoveSchema } from '@common/validation/preferredServiceOrganizationProviders';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { getServiceOrganizationSetting } from '@frontend/utils/apis/getServiceOrganizationSettings';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import { SortDirection } from '@frontend/utils/sortDirection';
import { validate } from '@frontend/utils/validation';
import type { IPreferredServiceOrganizationProviderWithServiceProviders } from './preferredServiceOrganizationProviderWithServiceProviders';
import { PersonalSettingsPreferenceListConsultationSearchOrganizationsNationWideModal } from './PersonalSettingsPreferenceListConsultationSearchOrganizationsNationWideModal';
import { PersonalSettingsPreferenceListConsultationTable } from './PersonalSettingsPreferenceListConsultationTable';
import {
    addPreferredServiceOrganizationProvidersForServiceOrganizationSetting,
    addUniqueLocalizedSpecializationText,
} from './personalSettingsPreferenceListHelper';

interface IPersonalSettingsPreferenceListConsultationSelectProvidersProps {
    isReadOnly: boolean;
    organizationId: string;
    serviceConsumerId: string;
}

interface IPreferredServiceOrganizationProviderWithSpecialization extends IPreferredServiceOrganizationProvider {
    serviceProviders: IServiceProvider[];
    specializationDescriptions: string[];
}

export function PersonalSettingsPreferenceListConsultationSelectProviders(
    props: IPersonalSettingsPreferenceListConsultationSelectProvidersProps
) {
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);

    if (isManager) {
        assertHasAuthorization(ManagerAuthorization.organizationRead);
    }

    const abortableFetch = useAbortableFetch();
    const language = useSelector((state: IReduxState) => state.language.code);

    const [serviceOrganizationSettingId, setServiceOrganizationSettingId] = useState<string>();
    const [serviceConsumer, setServiceConsumer] = useState<IServiceConsumer>();
    const [preferredOrganizationProviders, setPreferredOrganizationProviders] = useState<
        IPreferredServiceOrganizationProviderWithServiceProviders[]
    >([]);
    const [selectedProviderOrganizationIds, setSelectedProviderOrganizationIds] = useState<string[]>([]);
    const [showAddOrganizationNationWideModal, setShowAddOrganizationNationWideModal] = useState(false);
    const [sortKey, setSortKey] = useState('organization.name');
    const [sortDirection, setSortDirection] = useState(SortDirection.asc);

    useEffect(() => {
        load();
    }, [props.serviceConsumerId]);

    useEffect(() => {
        sortPreferredProviders();
    }, [sortKey, sortDirection]);

    if (!serviceConsumer) {
        return null;
    }

    return (
        <>
            <Card>
                <Card.Header
                    action={
                        !props.isReadOnly &&
                        (!isManager || hasOneAuthorization(ManagerAuthorization.organizationAddEdit)) && (
                            <Button
                                iconLeft="add"
                                onClick={() => setShowAddOrganizationNationWideModal(true)}
                            >
                                {translateKey('organization.add')}
                            </Button>
                        )
                    }
                    title={serviceConsumer.service.name}
                />
                {preferredOrganizationProviders.length === 0 && (
                    <Card.Body>
                        <Text mb={8}>
                            {translateKey(
                                'personalSettings.preferenceList.addFavoriteOrganizations',
                                serviceConsumer.service.name
                            )}
                        </Text>
                    </Card.Body>
                )}

                {preferredOrganizationProviders.length > 0 && (
                    <PersonalSettingsPreferenceListConsultationTable
                        isReadOnly={props.isReadOnly}
                        onChange={async (selected) => setSelectedProviderOrganizationIds(selected)}
                        onSort={handleSort}
                        selectedServiceOrganizationProviderOrganizationIds={selectedProviderOrganizationIds}
                        serviceOrganizationProviders={preferredOrganizationProviders}
                        sortDirection={sortDirection}
                        sortKey={sortKey}
                    />
                )}
            </Card>

            {showAddOrganizationNationWideModal && serviceOrganizationSettingId && (
                <PersonalSettingsPreferenceListConsultationSearchOrganizationsNationWideModal
                    isOpen={showAddOrganizationNationWideModal}
                    onAddOrganizations={handleAddOrganizations}
                    onClose={async () => setShowAddOrganizationNationWideModal(false)}
                    serviceId={serviceConsumer.service.id}
                    serviceOrganizationSettingId={serviceOrganizationSettingId}
                />
            )}

            <ActionBarShowAndRemoveSelection
                handleClose={handleClose}
                handleDelete={handleDeleteSelectedPreferredOrganizations}
                isOpen={selectedProviderOrganizationIds.length > 0}
                selectedIds={selectedProviderOrganizationIds}
            />
        </>
    );

    async function load() {
        const result = await abortableFetch.fetchJson(`/api/serviceConsumers/${props.serviceConsumerId}`, 'GET');

        const consumer = result.json;
        const serviceOrganizationSetting = await getServiceOrganizationSetting(
            props.organizationId,
            consumer.service.id
        );
        if (serviceOrganizationSetting) {
            setServiceOrganizationSettingId(serviceOrganizationSetting.id);
            const preferredOrganizations = (await loadPreferredOrganizationsWithProvidersAndSpecializations(
                serviceOrganizationSetting
            )) as IPreferredServiceOrganizationProviderWithServiceProviders[];
            setPreferredOrganizationProviders(preferredOrganizations);
        }

        setServiceConsumer(consumer);
        setSelectedProviderOrganizationIds([]);
    }

    async function handleSort(newSortKey: string, newSortDirection: SortDirection) {
        setSortKey(newSortKey);
        setSortDirection(newSortDirection);
    }

    function sortPreferredProviders() {
        setPreferredOrganizationProviders((current) => orderBy(current, sortKey, sortDirection));
    }

    async function loadPreferredOrganizationsWithProvidersAndSpecializations(
        serviceOrganizationSetting: IServiceOrganizationSettings
    ) {
        const isActiveQueryParameter = props.isReadOnly ? '' : '&isActive=true';
        const result = await abortableFetch.fetchJson(
            `/api/preferredServiceOrganizationProviders?serviceOrganizationSettingId=${serviceOrganizationSetting.id}&includeServiceProviderWithSpecializations=true${isActiveQueryParameter}`,
            'GET'
        );

        return addUniqueLocalizedSpecializationText(
            result.json.items as IPreferredServiceOrganizationProviderWithSpecialization[],
            language
        );
    }

    async function handleAddOrganizations(serviceOrganizationProviderOrganizationIds: string[]) {
        setShowAddOrganizationNationWideModal(false);
        await addPreferredServiceOrganizationProvidersForServiceOrganizationSetting(
            serviceOrganizationProviderOrganizationIds,
            serviceOrganizationSettingId!
        );
        await load();
    }

    async function handleDeleteSelectedPreferredOrganizations() {
        const filteredPreferredOrganizationProvidersIds = preferredOrganizationProviders
            .filter((organization) => selectedProviderOrganizationIds.includes(organization.organization.id))
            .map((organization) => organization.id);

        const { errors, item } = validate(preferredServiceOrganizationProviderRemoveSchema, {
            organizations: filteredPreferredOrganizationProvidersIds,
        });
        if (errors.size > 0) {
            return;
        }

        await abortableFetch.fetchJson('/api/preferredServiceOrganizationProviders', 'DELETE', item);
        setSelectedProviderOrganizationIds([]);
        await load();
    }

    async function handleClose() {
        setSelectedProviderOrganizationIds([]);
    }
}
